import React, { useState, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import DataSource from 'devextreme/data/data_source'
import CustomStore from 'devextreme/data/custom_store'
import { SelectBox } from 'devextreme-react/select-box'
import { Form, Formik } from 'formik'
import Div from '@jumbo/shared/Div'
import { useSnackbar } from 'notistack'
import JumboCardQuick from '@jumbo/components/JumboCardQuick'
import JumboAppTextField from '@jumbo/components/JumboAppTextField'
import LoadingButton from '@mui/lab/LoadingButton'
import SearchIcon from '@mui/icons-material/Search'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded'
import utilServices from 'app/services/util-services'
import customerServices from 'app/services/pages/reports/registrations/CustomerServices/customerServices'
import termServices from 'app/services/pages/courses/setup/termServices/termServices'
import classesService from 'app/services/pages/courses/setup/classesServices/classesService'
import sectionService from 'app/services/pages/courses/setup/sectionServices/sectionService'
import { useJumboTheme } from '@jumbo/hooks'
import { useMediaQuery } from '@mui/material'

const EnrolmentGirdFilter = ({ mutation, display, filterIconColor }) => {
    const navigate = useNavigate()
    const { enqueueSnackbar } = useSnackbar()
    const [filter, setFilter] = useState({ customerId: null })
    const { theme } = useJumboTheme()
    const sm = useMediaQuery(theme.breakpoints.down('sm'))

    let initialValues = {
        firstName: '',
        lastName: '',
        familyName: '',
    }

    const [termSelectedData, setTermSelectedData] = useState(null)
    const [classSelectedData, setClassSelectedData] = useState(null)
    const [sectionSelectedData, setSectionSelectedData] = useState(null)

    function handleError(error) {
        if (error.status === '401') {
            navigate('/profile/signout')
        } else if (error.detail) {
            enqueueSnackbar(error.detail, {
                variant: 'error',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right',
                },
            })
        } else {
            enqueueSnackbar(error, {
                variant: 'error',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right',
                },
            })
        }
    }

    const toFullName = useCallback((data) => {
        return data ? data.firstName + ' ' + data.lastName : ''
    }, [])

    const termYearNumber = useCallback((data) => {
        return data ? data.termYear + ' / ' + data.termNumber : ''
    }, [])

    const customerLookupList = useCallback(async (searchTerm) => {
        try {
            return await customerServices.getCustomerLookupList({ firstName: searchTerm })
        } catch (error) {
            handleError(error)
        }
    }, [])

    const customerLookupById = useCallback(async (key) => {
        try {
            return await customerServices.getCustomerLookupById(key)
        } catch (error) {
            handleError(error)
        }
    }, [])

    const customerLookupStore = useMemo(
        () =>
            new DataSource({
                store: new CustomStore({
                    key: 'id',
                    load: async function (loadOptions) {
                        let searchTerm = ''
                        if (loadOptions.searchValue) {
                            searchTerm = loadOptions.searchValue
                        }
                        return await customerLookupList(searchTerm)
                    },
                    byKey: function (key) {
                        if (key === '') return utilServices.emptyList()

                        return customerLookupById(key)
                    },
                    errorHandler: function (e) {
                        console.log(e)
                        return utilServices.emptyList()
                    },
                }),
                reshapeOnPush: true,
            }),
        []
    )

    const onTermValueChanged = async (e) => {
        if (e) {
            setTermSelectedData(e.id)
        } else {
            setTermSelectedData(null)
            setClassSelectedData(null)
            setSectionSelectedData(null)
        }
    }

    const onClassValueChanged = (e) => {
        if (e) {
            setClassSelectedData(e.id)
        } else {
            setClassSelectedData(null)
            setSectionSelectedData(null)
        }
    }

    const onSectionValueChanged = (e) => {
        if (e) {
            setSectionSelectedData(e)
        } else {
            setSectionSelectedData(null)
        }
    }

    const termLookupList = useCallback(async (searchTerm) => {
        try {
            return await termServices.getTermLookupList({ termYear: searchTerm })
        } catch (error) {
            handleError(error)
        }
    }, [])

    const termLookupById = useCallback(async (key) => {
        try {
            return await termServices.getTermLookupById(key)
        } catch (error) {
            handleError(error)
        }
    }, [])

    const termLookupsStore = useMemo(
        () =>
            new DataSource({
                store: new CustomStore({
                    key: 'id',
                    load: async function (loadOptions) {
                        let searchTerm = ''
                        if (loadOptions.searchValue) {
                            searchTerm = loadOptions.searchValue
                        }
                        return await termLookupList(searchTerm)
                    },
                    byKey: function (key) {
                        if (key === '') return utilServices.emptyList()

                        return termLookupById(key)
                    },
                    errorHandler: function (e) {
                        console.log(e)
                        return utilServices.emptyList()
                    },
                }),
                reshapeOnPush: true,
            }),
        []
    )

    const classLookupList = useCallback(
        async (searchTerm) => {
            try {
                if (utilServices.isNullOrUndefined(termSelectedData)) return { totalCount: 0, data: [] }
                return await classesService.getClassLookupList({
                    termId: termSelectedData,
                    className: searchTerm,
                })
            } catch (error) {
                handleError(error)
            }
        },
        [termSelectedData]
    )

    const classLookupById = useCallback(async (key) => {
        try {
            return await classesService.getClassLookupById(termSelectedData, key)
        } catch (error) {
            handleError(error)
        }
    }, [])

    const classLookupStore = useMemo(
        () =>
            new DataSource({
                store: new CustomStore({
                    key: 'id',
                    load: async function (loadOptions) {
                        let searchTerm = ''
                        if (loadOptions.searchValue) {
                            searchTerm = loadOptions.searchValue
                        }
                        return await classLookupList(searchTerm)
                    },
                    byKey: function (key) {
                        if (key === '') return utilServices.emptyList()

                        return classLookupById(key)
                    },
                    errorHandler: function (e) {
                        console.log(e)
                        return utilServices.emptyList()
                    },
                }),
                reshapeOnPush: true,
            }),
        [termSelectedData]
    )

    const sectionLookupList = useCallback(
        async (searchTerm) => {
            try {
                if (utilServices.isNullOrUndefined(classSelectedData)) return { totalCount: 0, data: [] }
                return await sectionService.getSectionLookupList({
                    classId: classSelectedData,
                    sectionName: searchTerm,
                })
            } catch (error) {
                handleError(error)
            }
        },
        [classSelectedData]
    )

    const sectionLookupById = useCallback(async (key) => {
        try {
            return await sectionService.getSectionLookupById(classSelectedData, key)
        } catch (error) {
            handleError(error)
        }
    }, [])

    const sectionLookupStore = useMemo(
        () =>
            new DataSource({
                store: new CustomStore({
                    key: 'id',
                    load: async function (loadOptions) {
                        let searchTerm = ''
                        if (loadOptions.searchValue) {
                            searchTerm = loadOptions.searchValue
                        }
                        return await sectionLookupList(searchTerm)
                    },
                    byKey: function (key) {
                        if (key === '') return utilServices.emptyList()

                        return sectionLookupById(key)
                    },
                    errorHandler: function (e) {
                        console.log(e)
                        return utilServices.emptyList()
                    },
                }),
                reshapeOnPush: true,
            }),
        [classSelectedData]
    )

    const onFormSubmit = async (firstName, lastName, familyName) => {
        await mutation.mutate({
            firstName: firstName,
            lastName: lastName,
            familyName: familyName ?? '',
            ...filter,
            termId: utilServices.isNullOrUndefined(termSelectedData) ? null : termSelectedData,
            classId: utilServices.isNullOrUndefined(classSelectedData) ? null : classSelectedData,
            sectionId: utilServices.isNullOrUndefined(sectionSelectedData) ? null : sectionSelectedData.id,
            sort: 'firstName',
            isDescending: false,
        })
    }

    const onFilterSubmit = async (data, { setSubmitting }) => {
        setSubmitting(true)
        await onFormSubmit(data.firstName, data.lastName, data.familyName)
        setSubmitting(false)
    }

    const handleClear = () => {
        filterIconColor(false)
        setFilter({ customerId: null })
        setTermSelectedData(null)
        setClassSelectedData(null)
        setSectionSelectedData(null)
    }

    const onCustomerSelectionChanged = (value) => {
        setFilter({ ...filter, customerId: value?.id })
    }

    return (
        <Formik
            validateOnChange={true}
            initialValues={initialValues}
            onSubmit={onFilterSubmit}
            onReset={onFilterSubmit}>
            {({ isSubmitting }) => (
                <Form noValidate autoComplete='off'>
                    <JumboCardQuick noWrapper sx={{ display: display, borderRadius: 0 }}>
                        <Div
                            className='grid-filter-parent'
                            style={{ display: 'flex', margin: '0% 1.5% 1.5% 1.5%', flexWrap: 'wrap' }}>
                            <Div sx={{ mr: 3, mt: 1.5 }}>
                                <Div>
                                    <Typography>Member First Name</Typography>
                                </Div>
                                <Div sx={{ mt: 0.5 }}>
                                    <JumboAppTextField style={{ width: 200 }} size='small' name='firstName' />
                                </Div>
                            </Div>
                            <Div sx={{ mr: 3, mt: 1.5 }}>
                                <Div>
                                    <Typography>Member Last Name</Typography>
                                </Div>
                                <Div sx={{ mt: 0.5 }}>
                                    <JumboAppTextField style={{ width: 200 }} size='small' name='lastName' />
                                </Div>
                            </Div>
                            <Div sx={{ mr: 3, mt: 1.5 }}>
                                <Div>
                                    <Typography>Family Name</Typography>
                                </Div>
                                <Div sx={{ mt: 0.5 }}>
                                    <JumboAppTextField
                                        style={{ width: 200 }}
                                        size='small'
                                        name='familyName'
                                        placeholder='Search by family name'
                                    />
                                </Div>
                            </Div>
                            <Div sx={{ mr: 3, mt: 1.5 }}>
                                <Div>
                                    <Typography>Customer Name</Typography>
                                </Div>
                                <Div sx={{ mt: 0.5 }}>
                                    <SelectBox
                                        style={{ width: 200 }}
                                        dataSource={customerLookupStore}
                                        value={filter.customerId}
                                        itemTemplate={toFullName}
                                        displayExpr={toFullName}
                                        searchEnabled={true}
                                        onValueChange={onCustomerSelectionChanged}
                                        placeholder='All'
                                        showClearButton={true}
                                    />
                                </Div>
                            </Div>
                            <Div sx={{ mr: 3, mt: 1.5 }}>
                                <Div>
                                    <Typography>Term</Typography>
                                </Div>
                                <Div sx={{ mt: 0.5 }}>
                                    <SelectBox
                                        style={{ width: 200 }}
                                        dataSource={termLookupsStore}
                                        itemTemplate={termYearNumber}
                                        displayExpr={termYearNumber}
                                        value={termSelectedData}
                                        searchEnabled={true}
                                        onValueChange={onTermValueChanged}
                                        placeholder='All'
                                        showClearButton={true}
                                    />
                                </Div>
                            </Div>
                            <Div sx={{ mr: 3, mt: 1.5 }}>
                                <Div>
                                    <Typography>Class Name</Typography>
                                </Div>
                                <Div sx={{ mt: 0.5 }}>
                                    <SelectBox
                                        style={{ width: 200 }}
                                        dataSource={classLookupStore}
                                        displayExpr={'className'}
                                        value={classSelectedData}
                                        searchEnabled={true}
                                        onValueChange={onClassValueChanged}
                                        placeholder='All'
                                        showClearButton={true}
                                    />
                                </Div>
                            </Div>
                            <Div sx={{ mr: sm ? 5 : 3, mt: 1.5 }}>
                                <Div>
                                    <Typography>Section Name</Typography>
                                </Div>
                                <Div sx={{ mt: 0.5 }}>
                                    <SelectBox
                                        style={{ width: 200 }}
                                        dataSource={sectionLookupStore}
                                        displayExpr={'sectionName'}
                                        value={sectionSelectedData}
                                        searchEnabled={true}
                                        onValueChange={onSectionValueChanged}
                                        placeholder='All'
                                        showClearButton={true}
                                    />
                                </Div>
                            </Div>
                            <Div className='grid-filter-Button' sx={{ mt: 5 }}>
                                <Stack direction={'row'} spacing={1} sx={{}}>
                                    <LoadingButton
                                        size={'small'}
                                        type='submit'
                                        onClick={() => filterIconColor(true)}
                                        variant={'contained'}
                                        loading={isSubmitting || mutation.isLoading}>
                                        <SearchIcon sx={{ fontSize: 18 }} /> Search
                                    </LoadingButton>
                                    <LoadingButton
                                        size={'small'}
                                        type='reset'
                                        onClick={handleClear}
                                        variant={'contained'}>
                                        <AutorenewRoundedIcon sx={{ fontSize: 18 }} /> Clear
                                    </LoadingButton>
                                </Stack>
                            </Div>
                        </Div>
                    </JumboCardQuick>
                </Form>
            )}
        </Formik>
    )
}

export default EnrolmentGirdFilter
